<template>
	<base-select
		:id="id"
		:value="value"
		:selected="selected"
		:options="options"
		:allowEmpty="allowEmpty"
		:showOnOneOption="showOnOneOption"
		v-on="$listeners"
		v-slot="{
			pSelectedOption,
			pOptionGroups,
			pExpanded,
			pInitial,
			mOpen,
			mClose,
			mToggle,
			mSelect,
			mSelectFirst,
			mSelectLast,
			mSelectDown,
			mSelectUp,
			cOneOption
		}"
	>
		<div :id="id" v-bind="$attrs" ref="parent" class="primary-select">
			<span v-if="!showLabelInButton" :id="labelId" class="visually-hidden">{{
				label
			}}</span>
			<h2>
				<slot name="button-before" />
				<BaseSelectButton
					:id="buttonId"
					ref="button"
					:class="[
						'primary-select-button',
						{ 'single-option-select': !cOneOption }
					]"
					:expanded="pExpanded"
					:labelledBy="ariaLabelledBy"
					:initial="pInitial"
					:toggle="mToggle"
					:open="mOpen"
					:selectDown="mSelectDown"
					:selectUp="mSelectUp"
					:oneOption="cOneOption"
				>
					<span class="inner" v-html="getButtonLabel(pSelectedOption)" />
					<span
						:class="['icon', pExpanded ? 'icon-up' : 'icon-down']"
						aria-hidden="true"
					/>
				</BaseSelectButton>
				<slot name="button-after" />
			</h2>
			<div ref="listboxAnchor" class="listbox-anchor">
				<BaseSelectList
					v-show="pExpanded"
					class="primary-select-list"
					:expanded="pExpanded"
					:labelledBy="labelId"
					:close="mClose"
					:selectFirst="mSelectFirst"
					:selectLast="mSelectLast"
					:selectDown="mSelectDown"
					:selectUp="mSelectUp"
					:selectedOption="pSelectedOption"
				>
					<template v-for="(options, group) in pOptionGroups">
						<li v-if="group" :key="group">
							<ul
								role="group"
								:aria-labelledby="groupLabelId(group)"
								class="primary-select-group"
							>
								<li
									:id="groupLabelId(group)"
									role="presentation"
									class="group-label"
								>
									{{ group }}
								</li>
								<BaseSelectOption
									v-for="option in options"
									:key="option.id"
									:id="option.id"
									class="primary-select-option"
									:class="{ disabled: option.disabled }"
									:aria-disabled="option.disabled ? 'true' : null"
									:selected="option.selected"
									:select="mSelect"
									v-html="getOptionSelectLabel(option)"
								/>
							</ul>
						</li>
						<template v-else>
							<BaseSelectOption
								v-for="option in options"
								:key="option.id"
								:id="option.id"
								class="primary-select-option"
								:class="{ disabled: option.disabled }"
								:aria-disabled="option.disabled ? 'true' : null"
								:selected="option.selected"
								:select="mSelect"
								v-html="getOptionSelectLabel(option)"
							/>
						</template>
					</template>
				</BaseSelectList>
			</div>
		</div>
	</base-select>
</template>

<script>
import { debounce } from "lodash";
import { Select } from "./mixins";
import BaseSelect from "./base/BaseSelect";
import BaseSelectButton from "./base/SelectButton";
import BaseSelectList from "./base/SelectList";
import BaseSelectOption from "./base/SelectOption";

/**
 * A select component with a Primary Select design theme.
 */
export default {
	name: "PrimarySelect",

	mixins: [Select],

	components: {
		BaseSelect,
		BaseSelectButton,
		BaseSelectList,
		BaseSelectOption
	},

	methods: {
		updateListboxBounds: debounce(async function () {
			await this.$nextTick();

			const parentBounds = this.$refs.parent.getBoundingClientRect();
			const buttonBounds = this.$refs.button.$el.getBoundingClientRect();

			this.$refs.listboxAnchor.style.left = `${
				buttonBounds.left - parentBounds.left
			}px`;
		}, 100)
	},

	created() {
		window.addEventListener("resize", this.updateListboxBounds);
	},

	mounted() {
		this.updateListboxBounds();
	},

	destroyed() {
		window.removeEventListener("resize", this.updateListboxBounds);
	}
};
</script>

<style lang="scss" scoped>
/* resets */
ul {
	list-style-type: none;
	margin: 0;
	padding: 0;

	li {
		margin: 0;
		padding: 0;
	}
}

h2 {
	font-size: 40px;
	letter-spacing: -0.15px;
	margin: 0;
}

.primary-select-button {
	position: relative;
	padding: 0 0 15px 0;
	font-size: inherit;
	line-height: 1;
	cursor: pointer;
	border: 0;
	border-bottom: 1px dotted $color--red;
	background-color: $color--white;
	appearance: none;

	.inner {
		flex-basis: 100%;
	}

	.icon {
		color: $color--red;
		display: inline-block;
		font-size: 16px;
		margin: 0 0 0 36px;
		vertical-align: bottom;
	}
}

.listbox-anchor {
	position: relative;
	top: 0;
}

.primary-select-list {
	position: absolute;
	top: 0;
	left: 0;
	z-index: 9999;
	margin: 0;
	padding: 0;
	list-style-type: none;
	font-size: 15px;
	font-weight: 400;
	line-height: 1;
	letter-spacing: -0.01em;
	background-color: $color--white;
	border-left: 2px solid $color--lighter-gray;
	border-right: 2px solid $color--lighter-gray;
	border-top: 2px solid $color--lighter-gray;
	box-shadow: 1px 1px 20px 0 rgba(0, 0, 0, 0.12);

	&.disabled {
		color: $color--light-gray;
		pointer-events: none;
	}
}

.primary-select-option {
	font-size: 15px;
	margin: 0;
	padding: 15px 23px 14px;
	border-bottom: 2px solid $color--lighter-gray;
	cursor: pointer;

	&:hover {
		background-color: $color--almost-white;
	}

	&.selected {
		background-color: $color--almost-white;
	}
}

.primary-select-group {
	font-size: 15px;
	font-weight: 400;
	line-height: 1;
	.primary-select-option {
		&:first-of-type {
			border-top: 2px solid $color--lighter-gray;
		}
	}
}

.group-label {
	font-weight: bold;
	padding: 32px 18px 14px;
	border-bottom: 2px solid $color--lighter-gray;
	margin: 0 0 4px 0;

	& + .primary-select-option {
		border-top: 2px solid $color--lighter-gray;
	}
}

// Hide icon if dropdown is disabled on one option.
.single-option-select {
	button {
		span {
			&.icon {
				&.icon-down {
					display: none;
				}
			}
		}
	}
}
</style>
